import { Building2, ChevronRight } from "lucide-react";
import { data, Link } from "react-router";
import { BreadcrumbJsonLd } from "~/components/seo/breadcrumb-jsonld";
import { envConfig } from "~/lib/env-validation";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import type { Route } from "./+types/odvetvi";

// --- Types ---

interface NaceCodeItem {
	code: string;
	description: string;
	section: string;
	sectionLabel: string;
	companyCount: number;
}

interface NaceCodesResponse {
	naceCodes: NaceCodeItem[];
}

// --- Loader ---

export async function loader() {
	const res = await fetch(`${envConfig.apiBaseUrl}/directory/nace-codes`);

	if (!res.ok) {
		throw data(null, { status: 500 });
	}

	const result: NaceCodesResponse = await res.json();
	return { naceCodes: result.naceCodes };
}

// --- Meta ---

export function meta() {
	return generateMetaTags({
		title: "Odvětví a obory podnikání | FirmIndex",
		description:
			"Přehled odvětví podle klasifikace NACE. Procházejte české firmy podle oboru podnikání a najděte společnosti ve vašem odvětví.",
		canonical: `${SITE_URL}/directory/odvetvi`,
	});
}

// --- Component ---

function formatCount(count: number): string {
	if (count >= 1000) {
		return `${Math.round(count / 1000)} tis.`;
	}
	return count.toLocaleString("cs-CZ");
}

export default function OdvetviPage({ loaderData }: Route.ComponentProps) {
	const { naceCodes } = loaderData;

	const totalCompanies = naceCodes.reduce((sum, n) => sum + n.companyCount, 0);

	return (
		<>
			<BreadcrumbJsonLd
				items={[
					{ name: "FirmIndex", url: "/" },
					{ name: "Odvětví", url: "/directory/odvetvi" },
				]}
			/>

			<div className="bg-background">
				{/* Header */}
				<div className="border-b border-gray-200 bg-card">
					<div className="mx-auto max-w-5xl px-4 py-8">
						<h1 className="text-2xl font-bold sm:text-3xl">Odvětví a obory podnikání</h1>
						<p className="mt-2 text-muted-foreground">
							Procházejte české firmy podle odvětví. Celkem{" "}
							<strong>{totalCompanies.toLocaleString("cs-CZ")}</strong> firem v{" "}
							<strong>{naceCodes.length}</strong> odvětvích.
						</p>
					</div>
				</div>

				{/* Content */}
				<main className="mx-auto max-w-5xl px-4 py-6">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
						{naceCodes.map((nace) => (
							<Link
								key={nace.code}
								to={`/directory/odvetvi/${nace.code}`}
								className="flex items-center justify-between gap-3 rounded-xl bg-card p-4 shadow-card transition-shadow hover:shadow-md"
							>
								<div className="flex items-start gap-3 min-w-0">
									<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-bold text-primary">
										{nace.code}
									</span>
									<div className="min-w-0">
										<h2 className="text-sm font-semibold leading-tight">{nace.sectionLabel}</h2>
										<p className="mt-0.5 text-xs text-muted-foreground">
											{formatCount(nace.companyCount)}{" "}
											{nace.companyCount === 1
												? "firma"
												: nace.companyCount < 5
													? "firmy"
													: "firem"}
										</p>
									</div>
								</div>
								<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
							</Link>
						))}
					</div>

					{naceCodes.length === 0 && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<Building2 className="h-12 w-12 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								Zatím nejsou k dispozici žádná odvětví.
							</p>
						</div>
					)}
				</main>
			</div>
		</>
	);
}
